/**
 * TeamStructureView — Org chart tree visualization for project team hierarchy.
 *
 * Renders role slots as a tree based on reports_to_slot_guid relationships.
 * Supports expand/collapse, responsive fallback (indented list below 768px),
 * and draft/commit editing in Structure Mode.
 *
 * Architecture ref: PRD-ProjectTeamStructure.docx (DEV-624)
 *
 * Notes:
 *   - Tree layout is computed in pure JS (no d3 dependency).
 *   - SVG connector lines + HTML node cards via foreignObject.
 *   - CSS custom property tokens per CLAUDE.md. Logical properties for RTL.
 *   - i18n via useAppTranslation('projectTeam').
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type {
  ProjectRoleSlotResponse,
  ProjectRoleAssignmentResponse,
} from "../../api/projectRoles";
import { updateTeamStructure } from "../../api/projectRoles";
import type { SecurityUserResponse } from "../../api/security";
import { mediaSrc } from "../../api/mediaUrl";
import { openTreePrintWindow } from "../../Data/TeamPrint/generateTreeHtml";
import styles from "./TeamStructureView.module.css";

// ════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════

interface Props {
  projectGuid: string;
  projectName: string;
  slots: ProjectRoleSlotResponse[];
  userMap: Map<string, SecurityUserResponse>;
  ownerGuid: string;
  canEdit: boolean;
  /**
   * DEV-1044. Lookup of slot GUID (uppercase) → bundle display name.
   * Each node card and indented row resolves its own bundle from this map
   * and renders it next to the role label. Slots absent from the map have
   * no active grant; they show a muted "No bundle" indicator.
   */
  slotBundleNameMap?: Map<string, string>;
  onRefetch: () => void;
  onEditSlot?: (slot: ProjectRoleSlotResponse) => void;
  onAssignSlot?: (slot: ProjectRoleSlotResponse) => void;
  onViewAssignment?: (assignment: ProjectRoleAssignmentResponse, slot: ProjectRoleSlotResponse) => void;
}

interface TreeNode {
  slot: ProjectRoleSlotResponse;
  children: TreeNode[];
  collapsed: boolean;
  /** Computed layout positions */
  x: number;
  y: number;
  width: number;
  subtreeWidth: number;
}

// ════════════════════════════════════════════════════════════════
// Constants
// ════════════════════════════════════════════════════════════════

const NODE_WIDTH = 230;
const NODE_HEIGHT = 110;
const H_GAP = 24;
const V_GAP = 60;
const CONNECTOR_RADIUS = 8;

// ════════════════════════════════════════════════════════════════
// Helpers
// ════════════════════════════════════════════════════════════════

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

function profileImgUrl(user: SecurityUserResponse): string | null {
  return mediaSrc(user.profileImageUrl);
}

/**
 * Build a forest (array of root TreeNode) from flat slot array.
 * Slots with no reports_to or whose parent is missing become roots.
 */
function buildForest(
  slots: ProjectRoleSlotResponse[],
  collapsedSet: Set<string>
): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>();
  const childrenMap = new Map<string, TreeNode[]>();

  // Create nodes
  for (const slot of slots) {
    const node: TreeNode = {
      slot,
      children: [],
      collapsed: collapsedSet.has(slot.roleSlotGuid.toUpperCase()),
      x: 0,
      y: 0,
      width: NODE_WIDTH,
      subtreeWidth: NODE_WIDTH,
    };
    nodeMap.set(slot.roleSlotGuid.toUpperCase(), node);
  }

  // Build parent→children mapping
  for (const slot of slots) {
    const parentGuid = slot.reportsToSlotGuid;
    if (parentGuid) {
      const parentKey = parentGuid.toUpperCase();
      if (nodeMap.has(parentKey)) {
        if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
        const node = nodeMap.get(slot.roleSlotGuid.toUpperCase())!;
        childrenMap.get(parentKey)!.push(node);
      }
    }
  }

  // Assign children and sort
  for (const [key, children] of childrenMap) {
    const parent = nodeMap.get(key);
    if (parent) {
      parent.children = children.sort(
        (a, b) => a.slot.sortOrder - b.slot.sortOrder
      );
    }
  }

  // Identify roots: slots with no parent or parent not in the set
  const roots: TreeNode[] = [];
  for (const slot of slots) {
    const parentGuid = slot.reportsToSlotGuid;
    if (!parentGuid || !nodeMap.has(parentGuid.toUpperCase())) {
      roots.push(nodeMap.get(slot.roleSlotGuid.toUpperCase())!);
    }
  }

  return roots.sort((a, b) => a.slot.sortOrder - b.slot.sortOrder);
}

/**
 * Compute layout positions for a tree (recursive, bottom-up width first).
 * Returns total width consumed.
 */
function layoutTree(nodes: TreeNode[], startX: number, startY: number): number {
  let currentX = startX;

  for (const node of nodes) {
    node.y = startY;

    if (node.children.length === 0 || node.collapsed) {
      // Leaf or collapsed: occupies NODE_WIDTH
      node.x = currentX;
      node.subtreeWidth = NODE_WIDTH;
      currentX += NODE_WIDTH + H_GAP;
    } else {
      // Has visible children: lay them out first, then center parent
      const childStartX = currentX;
      const childY = startY + NODE_HEIGHT + V_GAP;
      const childrenWidth = layoutTree(node.children, childStartX, childY);

      node.subtreeWidth = Math.max(NODE_WIDTH, childrenWidth);

      // Center parent over children
      const firstChild = node.children[0];
      const lastChild = node.children[node.children.length - 1];
      const childSpan = (lastChild.x + NODE_WIDTH) - firstChild.x;
      node.x = firstChild.x + (childSpan - NODE_WIDTH) / 2;

      currentX = childStartX + node.subtreeWidth + H_GAP;
    }
  }

  // Total width (subtract trailing gap)
  return Math.max(0, currentX - startX - H_GAP);
}

/**
 * Compute total tree bounds after layout.
 */
function treeBounds(nodes: TreeNode[]): { width: number; height: number } {
  let maxX = 0;
  let maxY = 0;

  function walk(n: TreeNode) {
    maxX = Math.max(maxX, n.x + NODE_WIDTH);
    maxY = Math.max(maxY, n.y + NODE_HEIGHT);
    if (!n.collapsed) {
      for (const child of n.children) walk(child);
    }
  }

  for (const root of nodes) walk(root);
  return { width: maxX, height: maxY };
}

// ════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════

const TeamStructureView: React.FC<Props> = ({
  projectGuid,
  projectName,
  slots,
  userMap,
  ownerGuid,
  canEdit,
  slotBundleNameMap,
  onRefetch,
  onEditSlot,
  onAssignSlot,
  onViewAssignment,
}) => {
  const { t } = useAppTranslation("projectTeam");

  // ── Collapse state ──
  const [collapsedSet, setCollapsedSet] = useState<Set<string>>(new Set());
  const toggleCollapse = useCallback((slotGuid: string) => {
    setCollapsedSet((prev) => {
      const next = new Set(prev);
      const key = slotGuid.toUpperCase();
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── Responsive: detect narrow viewport ──
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsNarrow(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ── Structure Mode (draft/commit editing) ──
  const [structureMode, setStructureMode] = useState(false);
  const [draftParentMap, setDraftParentMap] = useState<Map<string, string | null>>(new Map());
  const [saving, setSaving] = useState(false);

  const enterStructureMode = useCallback(() => {
    setStructureMode(true);
    // Initialize draft from current state (exclude closed slots)
    const map = new Map<string, string | null>();
    for (const slot of slots) {
      if (slot.status === "closed") continue;
      map.set(slot.roleSlotGuid.toUpperCase(), slot.reportsToSlotGuid?.toUpperCase() ?? null);
    }
    setDraftParentMap(map);
  }, [slots]);

  const cancelStructureMode = useCallback(() => {
    setStructureMode(false);
    setDraftParentMap(new Map());
  }, []);

  const commitStructure = useCallback(async () => {
    setSaving(true);
    try {
      // Only send entries that actually changed from their original state
      const entries: { roleSlotGuid: string; reportsToSlotGuid: string | null }[] = [];
      for (const slot of slots) {
        if (slot.status === "closed") continue;
        const key = slot.roleSlotGuid.toUpperCase();
        const original = slot.reportsToSlotGuid?.toUpperCase() ?? null;
        const draft = draftParentMap.get(key) ?? null;
        if (original !== draft) {
          entries.push({
            roleSlotGuid: slot.roleSlotGuid,
            reportsToSlotGuid: draft ? draft.toLowerCase() : null,
          });
        }
      }
      if (entries.length === 0) return;
      await updateTeamStructure(projectGuid, { entries });
      setStructureMode(false);
      setDraftParentMap(new Map());
      onRefetch();
    } catch (err) {
      console.error("[TeamStructureView] Commit structure failed:", err);
    } finally {
      setSaving(false);
    }
  }, [projectGuid, slots, draftParentMap, onRefetch]);

  // ── Build tree from slots (using draft overrides if in structure mode) ──
  const effectiveSlots = useMemo(() => {
    if (!structureMode) return slots;
    // Apply draft parent overrides
    return slots.map((slot) => {
      const key = slot.roleSlotGuid.toUpperCase();
      if (draftParentMap.has(key)) {
        const draftParent = draftParentMap.get(key);
        return {
          ...slot,
          reportsToSlotGuid: draftParent ?? null,
        };
      }
      return slot;
    });
  }, [slots, structureMode, draftParentMap]);

  // Filter to non-closed, non-draft slots for the tree
  const visibleSlots = useMemo(
    () => effectiveSlots.filter((s) => s.status !== "closed"),
    [effectiveSlots]
  );

  const hasHierarchy = useMemo(
    () => visibleSlots.some((s) => s.reportsToSlotGuid != null),
    [visibleSlots]
  );

  const forest = useMemo(
    () => buildForest(visibleSlots, collapsedSet),
    [visibleSlots, collapsedSet]
  );

  // Layout
  const padding = 24;
  useMemo(() => layoutTree(forest, padding, padding), [forest]);
  const bounds = useMemo(() => treeBounds(forest), [forest]);

  // ── Drag state for structure mode (reparenting) ──
  const [dragSource, setDragSource] = useState<string | null>(null);
  const [dragTarget, setDragTarget] = useState<string | null>(null);

  const handleStructureDragStart = useCallback((slotGuid: string) => (e: React.DragEvent) => {
    if (!structureMode) return;
    setDragSource(slotGuid.toUpperCase());
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", slotGuid);
  }, [structureMode]);

  const handleStructureDragOver = useCallback((slotGuid: string) => (e: React.DragEvent) => {
    if (!structureMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragTarget(slotGuid.toUpperCase());
  }, [structureMode]);

  const handleStructureDragLeave = useCallback(() => {
    setDragTarget(null);
  }, []);

  const handleStructureDrop = useCallback((targetSlotGuid: string) => (e: React.DragEvent) => {
    e.preventDefault();
    if (!structureMode) return;

    const sourceGuid = e.dataTransfer.getData("text/plain").toUpperCase();
    const targetKey = targetSlotGuid.toUpperCase();
    setDragSource(null);
    setDragTarget(null);

    if (!sourceGuid || sourceGuid === targetKey) return;

    // Simple cycle check: walk ancestors of target in draft map
    let cursor: string | null = targetKey;
    const visited = new Set<string>();
    while (cursor) {
      if (cursor === sourceGuid) return; // Would create cycle
      if (visited.has(cursor)) break;
      visited.add(cursor);
      cursor = draftParentMap.get(cursor) ?? null;
    }

    // Apply: source now reports to target
    setDraftParentMap((prev) => {
      const next = new Map(prev);
      next.set(sourceGuid, targetKey);
      return next;
    });
  }, [structureMode, draftParentMap]);

  const handleDropToRoot = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!structureMode) return;

    const sourceGuid = e.dataTransfer.getData("text/plain").toUpperCase();
    setDragSource(null);
    setDragTarget(null);

    if (!sourceGuid) return;

    // Make root
    setDraftParentMap((prev) => {
      const next = new Map(prev);
      next.set(sourceGuid, null);
      return next;
    });
  }, [structureMode]);

  const handleStructureDragEnd = useCallback(() => {
    setDragSource(null);
    setDragTarget(null);
  }, []);

  // Check if draft has changes
  const hasChanges = useMemo(() => {
    if (!structureMode) return false;
    for (const slot of slots) {
      const key = slot.roleSlotGuid.toUpperCase();
      const current = slot.reportsToSlotGuid?.toUpperCase() ?? null;
      const draft = draftParentMap.get(key) ?? null;
      if (current !== draft) return true;
    }
    return false;
  }, [structureMode, slots, draftParentMap]);

  // ── Render: narrow → indented list fallback ──
  if (isNarrow) {
    return (
      <div className={styles.container}>
        {canEdit && hasHierarchy && (
          <div className={styles.toolbar}>
            <span className={styles.viewLabel}>
              {t("structure.indentedView")}
            </span>
          </div>
        )}
        <div className={styles.indentedList}>
          {forest.map((root) => (
            <IndentedNode
              key={root.slot.roleSlotGuid}
              node={root}
              depth={0}
              slotBundleNameMap={slotBundleNameMap}
              userMap={userMap}
              ownerGuid={ownerGuid}
              onToggleCollapse={toggleCollapse}
              onViewAssignment={onViewAssignment}
              t={t}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Render: wide → SVG org chart ──
  const svgWidth = bounds.width + padding * 2;
  const svgHeight = bounds.height + padding * 2;

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        {!structureMode && canEdit && (
          <button
            type="button"
            className={styles.toolbarBtn}
            onClick={enterStructureMode}
          >
            {t("structure.editStructure")}
          </button>
        )}
        {!structureMode && (
          <button
            type="button"
            className={styles.toolbarBtn}
            onClick={() => openTreePrintWindow(slots, userMap, ownerGuid, projectName)}
            title={t("structure.printBtn") || "Print"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            {t("structure.printBtn") || "Print"}
          </button>
        )}
        {structureMode && (
          <>
            <span className={styles.modeLabel}>
              {t("structure.structureMode")}
            </span>
            <span className={styles.modeHint}>
              {t("structure.dragToReparent")}
            </span>
            <div className={styles.toolbarSpacer} />
            <button
              type="button"
              className={styles.toolbarBtnCancel}
              onClick={cancelStructureMode}
              disabled={saving}
            >
              {t("structure.cancel")}
            </button>
            <button
              type="button"
              className={styles.toolbarBtnCommit}
              onClick={commitStructure}
              disabled={saving || !hasChanges}
            >
              {saving ? t("structure.saving") : t("structure.commit")}
            </button>
          </>
        )}
      </div>

      {/* Drop zone for making nodes root */}
      {structureMode && (
        <div
          className={styles.rootDropZone}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
          onDrop={handleDropToRoot}
        >
          {t("structure.dropToMakeRoot")}
        </div>
      )}

      {/* SVG tree */}
      <div className={styles.scrollWrapper}>
        <svg
          className={styles.svg}
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        >
          {/* Connector lines */}
          {forest.map((root) => (
            <ConnectorLines key={root.slot.roleSlotGuid} node={root} />
          ))}

          {/* Node cards via foreignObject */}
          {forest.map((root) => (
            <NodeCards
              key={root.slot.roleSlotGuid}
              node={root}
              userMap={userMap}
              ownerGuid={ownerGuid}
              canEdit={canEdit}
              structureMode={structureMode}
              dragSource={dragSource}
              dragTarget={dragTarget}
              slotBundleNameMap={slotBundleNameMap}
              onToggleCollapse={toggleCollapse}
              onEditSlot={onEditSlot}
              onAssignSlot={onAssignSlot}
              onViewAssignment={onViewAssignment}
              onStructureDragStart={handleStructureDragStart}
              onStructureDragOver={handleStructureDragOver}
              onStructureDragLeave={handleStructureDragLeave}
              onStructureDrop={handleStructureDrop}
              onStructureDragEnd={handleStructureDragEnd}
              t={t}
            />
          ))}
        </svg>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// SVG Connector Lines
// ════════════════════════════════════════════════════════════════

const ConnectorLines: React.FC<{ node: TreeNode }> = ({ node }) => {
  if (node.collapsed || node.children.length === 0) return null;

  const parentCX = node.x + NODE_WIDTH / 2;
  const parentBottomY = node.y + NODE_HEIGHT;

  return (
    <>
      {node.children.map((child) => {
        const childCX = child.x + NODE_WIDTH / 2;
        const childTopY = child.y;
        const midY = parentBottomY + V_GAP / 2;

        // Stepped connector: down from parent, horizontal, down to child
        const d = [
          `M ${parentCX} ${parentBottomY}`,
          `L ${parentCX} ${midY - CONNECTOR_RADIUS}`,
          // Rounded corner toward child
          parentCX !== childCX
            ? `Q ${parentCX} ${midY} ${parentCX + Math.sign(childCX - parentCX) * CONNECTOR_RADIUS} ${midY}`
            : `L ${parentCX} ${midY}`,
          `L ${childCX - Math.sign(childCX - parentCX) * CONNECTOR_RADIUS} ${midY}`,
          parentCX !== childCX
            ? `Q ${childCX} ${midY} ${childCX} ${midY + CONNECTOR_RADIUS}`
            : "",
          `L ${childCX} ${childTopY}`,
        ].join(" ");

        return (
          <path
            key={child.slot.roleSlotGuid}
            d={d}
            className={styles.connectorLine}
            fill="none"
          />
        );
      })}

      {/* Recurse */}
      {node.children.map((child) => (
        <ConnectorLines key={child.slot.roleSlotGuid} node={child} />
      ))}
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// SVG foreignObject Node Cards
// ════════════════════════════════════════════════════════════════

interface NodeCardsProps {
  node: TreeNode;
  userMap: Map<string, SecurityUserResponse>;
  ownerGuid: string;
  canEdit: boolean;
  structureMode: boolean;
  dragSource: string | null;
  dragTarget: string | null;
  /** DEV-1044. Slot → bundle-name lookup (threaded down recursively). */
  slotBundleNameMap?: Map<string, string>;
  onToggleCollapse: (guid: string) => void;
  onEditSlot?: (slot: ProjectRoleSlotResponse) => void;
  onAssignSlot?: (slot: ProjectRoleSlotResponse) => void;
  onViewAssignment?: (a: ProjectRoleAssignmentResponse, s: ProjectRoleSlotResponse) => void;
  onStructureDragStart: (guid: string) => (e: React.DragEvent) => void;
  onStructureDragOver: (guid: string) => (e: React.DragEvent) => void;
  onStructureDragLeave: () => void;
  onStructureDrop: (guid: string) => (e: React.DragEvent) => void;
  onStructureDragEnd: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const NodeCards: React.FC<NodeCardsProps> = ({
  node,
  userMap,
  ownerGuid,
  canEdit,
  structureMode,
  dragSource,
  dragTarget,
  slotBundleNameMap,
  onToggleCollapse,
  onEditSlot,
  onAssignSlot,
  onViewAssignment,
  onStructureDragStart,
  onStructureDragOver,
  onStructureDragLeave,
  onStructureDrop,
  onStructureDragEnd,
  t,
}) => {
  const slot = node.slot;
  const key = slot.roleSlotGuid.toUpperCase();
  const isDragSource = dragSource === key;
  const isDragTarget = dragTarget === key;
  // DEV-1044. Bundle this slot carries (if any) — surfaced under role label.
  const bundleName = slotBundleNameMap?.get(key) ?? null;

  const activeAssignments = useMemo(
    () => (slot.assignments || []).filter((a) => a.status === "active"),
    [slot.assignments]
  );

  // Resolve users for stacked avatars (show up to 4, overflow as "+N")
  const MAX_VISIBLE_AVATARS = 4;
  const assignedUsers = useMemo(() => {
    if (activeAssignments.length > 0) {
      return activeAssignments.map((a) => userMap.get(a.userGuid.toUpperCase()));
    }
    if (slot.isOwnerDefault) {
      const owner = userMap.get(ownerGuid.toUpperCase());
      return owner ? [owner] : [];
    }
    return [];
  }, [activeAssignments, slot.isOwnerDefault, userMap, ownerGuid]);

  const visibleUsers = assignedUsers.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = assignedUsers.length - visibleUsers.length;

  const hasChildren = node.children.length > 0;

  const cardClasses = [
    styles.nodeCard,
    isDragSource ? styles.nodeCardDragging : "",
    isDragTarget ? styles.nodeCardDropTarget : "",
    structureMode ? styles.nodeCardStructureMode : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      <foreignObject
        x={node.x}
        y={node.y}
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
      >
        <div
          className={cardClasses}
          draggable={structureMode && canEdit}
          onDragStart={onStructureDragStart(slot.roleSlotGuid)}
          onDragOver={onStructureDragOver(slot.roleSlotGuid)}
          onDragLeave={onStructureDragLeave}
          onDrop={onStructureDrop(slot.roleSlotGuid)}
          onDragEnd={onStructureDragEnd}
        >
          {/* Avatar(s) — large single, stacked multi */}
          {assignedUsers.length === 1 && assignedUsers[0] ? (
            <div className={styles.nodeAvatarSingle}>
              {profileImgUrl(assignedUsers[0]) ? (
                <img
                  src={profileImgUrl(assignedUsers[0])!}
                  alt=""
                  className={styles.nodeAvatarImg}
                />
              ) : (
                <span className={styles.nodeAvatarInitials}>
                  {getInitials(assignedUsers[0].displayName || assignedUsers[0].name)}
                </span>
              )}
            </div>
          ) : (
            <div className={styles.nodeAvatarStack}>
              {visibleUsers.length > 0 ? (
                visibleUsers.map((user, idx) => (
                  <div
                    key={user?.userGuid || idx}
                    className={styles.nodeAvatarStacked}
                    style={{ zIndex: visibleUsers.length - idx }}
                  >
                    {user && profileImgUrl(user) ? (
                      <img
                        src={profileImgUrl(user)!}
                        alt=""
                        className={styles.nodeAvatarImg}
                      />
                    ) : user ? (
                      <span className={styles.nodeAvatarInitials}>
                        {getInitials(user.displayName || user.name)}
                      </span>
                    ) : (
                      <span className={styles.nodeAvatarEmpty}>?</span>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.nodeAvatarStacked}>
                  <span className={styles.nodeAvatarEmpty}>?</span>
                </div>
              )}
              {overflowCount > 0 && (
                <div className={styles.nodeAvatarOverflow}>
                  +{overflowCount}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className={styles.nodeInfo}>
            <div className={styles.nodeRoleLabel}>{slot.roleLabel}</div>
            {/* DEV-1044. Bundle line under the role label. Shows the
                operational bundle's name, or a muted "no bundle" indicator
                for legacy slots without an active grant. */}
            {bundleName ? (
              <div className={styles.nodeBundle} title={bundleName}>
                {bundleName}
              </div>
            ) : (
              <div className={styles.nodeBundleNone}>
                {t("card.noBundle")}
              </div>
            )}
            {assignedUsers.length === 1 && assignedUsers[0] && (
              <div className={styles.nodePersonName}>
                {assignedUsers[0].displayName || assignedUsers[0].name}
              </div>
            )}
            {assignedUsers.length > 1 && (
              <div className={styles.nodePersonName}>
                {assignedUsers.length} {t("structure.assigned")}
              </div>
            )}
            {assignedUsers.length === 0 && (
              <div className={styles.nodeVacant}>
                {t("structure.vacant")}
              </div>
            )}
          </div>

          {/* Collapse toggle */}
          {hasChildren && (
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse(slot.roleSlotGuid);
              }}
              title={node.collapsed ? t("structure.expand") : t("structure.collapse")}
            >
              {node.collapsed ? `▸ ${node.children.length}` : "▾"}
            </button>
          )}

          {/* Quick actions (non-structure mode) */}
          {canEdit && !structureMode && (
            <div className={styles.nodeActions}>
              {onEditSlot && (
                <button
                  type="button"
                  className={styles.nodeActionBtn}
                  onClick={(e) => { e.stopPropagation(); onEditSlot(slot); }}
                  title={t("card.edit")}
                >
                  ✎
                </button>
              )}
              {onAssignSlot && (
                <button
                  type="button"
                  className={styles.nodeActionBtn}
                  onClick={(e) => { e.stopPropagation(); onAssignSlot(slot); }}
                  title={t("card.assign")}
                >
                  +
                </button>
              )}
            </div>
          )}
        </div>
      </foreignObject>

      {/* Recurse children if not collapsed */}
      {!node.collapsed && node.children.map((child) => (
        <NodeCards
          key={child.slot.roleSlotGuid}
          node={child}
          userMap={userMap}
          ownerGuid={ownerGuid}
          canEdit={canEdit}
          structureMode={structureMode}
          dragSource={dragSource}
          dragTarget={dragTarget}
          slotBundleNameMap={slotBundleNameMap}
          onToggleCollapse={onToggleCollapse}
          onEditSlot={onEditSlot}
          onAssignSlot={onAssignSlot}
          onViewAssignment={onViewAssignment}
          onStructureDragStart={onStructureDragStart}
          onStructureDragOver={onStructureDragOver}
          onStructureDragLeave={onStructureDragLeave}
          onStructureDrop={onStructureDrop}
          onStructureDragEnd={onStructureDragEnd}
          t={t}
        />
      ))}
    </>
  );
};

// ════════════════════════════════════════════════════════════════
// Indented List Fallback (narrow viewports)
// ════════════════════════════════════════════════════════════════

interface IndentedNodeProps {
  node: TreeNode;
  depth: number;
  /** DEV-1044. Slot → bundle-name lookup (threaded down recursively). */
  slotBundleNameMap?: Map<string, string>;
  userMap: Map<string, SecurityUserResponse>;
  ownerGuid: string;
  onToggleCollapse: (guid: string) => void;
  onViewAssignment?: (a: ProjectRoleAssignmentResponse, s: ProjectRoleSlotResponse) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const IndentedNode: React.FC<IndentedNodeProps> = ({
  node,
  depth,
  slotBundleNameMap,
  userMap,
  ownerGuid,
  onToggleCollapse,
  onViewAssignment,
  t,
}) => {
  const slot = node.slot;
  const activeAssignments = (slot.assignments || []).filter((a) => a.status === "active");
  const primaryUser = activeAssignments.length > 0
    ? userMap.get(activeAssignments[0].userGuid.toUpperCase())
    : slot.isOwnerDefault
      ? userMap.get(ownerGuid.toUpperCase())
      : undefined;
  const hasChildren = node.children.length > 0;
  // DEV-1044. Bundle this slot carries (if any) — surfaced in the indented row.
  const bundleName = slotBundleNameMap?.get(slot.roleSlotGuid.toUpperCase()) ?? null;

  return (
    <>
      <div
        className={styles.indentedRow}
        style={{ paddingInlineStart: `${depth * 24 + 8}px` }}
      >
        {/* Collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            className={styles.indentedToggle}
            onClick={() => onToggleCollapse(slot.roleSlotGuid)}
          >
            {node.collapsed ? "▸" : "▾"}
          </button>
        ) : (
          <span className={styles.indentedToggleSpacer} />
        )}

        {/* Mini avatar */}
        <div className={styles.indentedAvatar}>
          {primaryUser ? (
            profileImgUrl(primaryUser) ? (
              <img
                src={profileImgUrl(primaryUser)!}
                alt=""
                className={styles.indentedAvatarImg}
              />
            ) : (
              <span className={styles.indentedAvatarInitials}>
                {getInitials(primaryUser.displayName || primaryUser.name)}
              </span>
            )
          ) : (
            <span className={styles.indentedAvatarEmpty}>?</span>
          )}
        </div>

        {/* Text */}
        <div className={styles.indentedInfo}>
          <span className={styles.indentedRole}>{slot.roleLabel}</span>
          {/* DEV-1044. Bundle name — small, secondary line under the role. */}
          {bundleName ? (
            <span className={styles.indentedBundle} title={bundleName}>
              {bundleName}
            </span>
          ) : (
            <span className={styles.indentedBundleNone}>
              {t("card.noBundle")}
            </span>
          )}
          {primaryUser && (
            <span className={styles.indentedName}>
              {primaryUser.displayName || primaryUser.name}
            </span>
          )}
          {!primaryUser && (
            <span className={styles.indentedVacant}>
              {t("structure.vacant")}
            </span>
          )}
        </div>

        {/* Count badge */}
        {hasChildren && node.collapsed && (
          <span className={styles.indentedChildCount}>
            {node.children.length}
          </span>
        )}
      </div>

      {/* Children */}
      {!node.collapsed && node.children.map((child) => (
        <IndentedNode
          key={child.slot.roleSlotGuid}
          node={child}
          depth={depth + 1}
          slotBundleNameMap={slotBundleNameMap}
          userMap={userMap}
          ownerGuid={ownerGuid}
          onToggleCollapse={onToggleCollapse}
          onViewAssignment={onViewAssignment}
          t={t}
        />
      ))}
    </>
  );
};

export default TeamStructureView;
